import { useState } from 'react';
import { useGameStore } from './store';
import { useAutoAdvance } from './hooks/useAutoAdvance';
import { useCardExpiration } from './hooks/useCardExpiration';
import { AdminPanel } from './components/AdminPanel';
import { Header } from './components/Header';
import { DailyDraw } from './components/DailyDraw';
import { HousingDisplay } from './components/HousingDisplay';
import { CardVault } from './components/CardVault';
import { Shop } from './components/Shop';

function Dashboard() {
  const deposits = useGameStore((state) => state.deposits);
  const cards = useGameStore((state) => state.cards);
  const streakStartDate = useGameStore((state) => state.streakStartDate);
  const gameDate = useGameStore((state) => state.gameDate);

  // Calculate stats
  const totalSavings = deposits.reduce((sum, d) => sum + d.amount, 0);
  const activeCardsCount = cards.filter(c => c.isActive).length;

  // Calculate days since start
  let daysSinceStart = 0;
  if (streakStartDate) {
    const start = new Date(streakStartDate);
    const current = new Date(gameDate);
    const msPerDay = 24 * 60 * 60 * 1000;
    daysSinceStart = Math.floor((current - start) / msPerDay);
  }

  return (
    <div className="w-full max-w-md">
      {/* Daily Draw */}
      <DailyDraw />

      {/* Housing Display */}
      <HousingDisplay />

      {/* Stats Section */}
      <div className="mt-6 grid grid-cols-3 gap-3">
        <div className="bg-gray-800 border border-gray-700 rounded p-3 text-center">
          <div className="text-gold font-pixel text-lg">${totalSavings.toLocaleString()}</div>
          <div className="text-gray-400 font-pixel text-xs mt-1">Total Saved</div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded p-3 text-center">
          <div className="text-teal font-pixel text-lg">{activeCardsCount}</div>
          <div className="text-gray-400 font-pixel text-xs mt-1">Active Cards</div>
        </div>

        <div className="bg-gray-800 border border-gray-700 rounded p-3 text-center">
          <div className="text-purple-400 font-pixel text-lg">{daysSinceStart}</div>
          <div className="text-gray-400 font-pixel text-xs mt-1">Days Active</div>
        </div>
      </div>
    </div>
  );
}

function App() {
  useAutoAdvance();
  useCardExpiration(); // Process expired cards when game date changes

  const [activeTab, setActiveTab] = useState('home');

  const tabs = [
    { id: 'home', label: 'Home' },
    { id: 'shop', label: 'Shop' },
    { id: 'cards', label: 'Cards' }
  ];

  return (
    <div className="min-h-screen bg-dark flex flex-col items-center">
      {/* Header - renders once for all tabs */}
      <Header />

      {/* Tab Navigation */}
      <div className="w-full max-w-md mx-auto px-5 mb-4">
        <div className="flex flex-wrap gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex-1 px-3 sm:px-4 py-2 font-pixel text-sm border-2 rounded
                transition-all duration-200
                ${activeTab === tab.id
                  ? 'bg-teal text-dark border-teal'
                  : 'bg-gray-800 text-gray-400 border-gray-600 hover:border-gray-500 hover:text-gray-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="container-game pb-20">
        {activeTab === 'home' && <Dashboard />}
        {activeTab === 'shop' && <Shop />}
        {activeTab === 'cards' && <CardVault />}
      </div>

      {/* Admin Panel - visible on all tabs */}
      <AdminPanel />
    </div>
  );
}

export default App;
